/**
 * Provedores do react-admin: dados (PostgREST do Supabase) e autenticação (Supabase Auth).
 * Papéis e "ativo" vêm da tabela `usuario` (D24); o RLS do banco é quem garante o acesso.
 */
import type { AuthProvider } from "react-admin";
import { supabaseAuthProvider, supabaseDataProvider } from "ra-supabase";
import { supabase, supabaseChavePublica, supabaseUrl } from "./supabase";

export type CodigoPapel = "admin" | "usuario";

export interface Permissoes {
  papel: CodigoPapel;
}

interface PerfilUsuario {
  id: string;
  nome: string;
  email: string;
  ativo: boolean;
  papel: { codigo: string } | null;
}

/** Lê o perfil do usuário logado. `null` = sem perfil (não deveria acontecer: o trigger cria). */
async function lerPerfil(usuarioId: string): Promise<PerfilUsuario | null> {
  const { data, error } = await supabase
    .from("usuario")
    .select("id, nome, email, ativo, papel:papel_id(codigo)")
    .eq("id", usuarioId)
    .maybeSingle<PerfilUsuario>();
  if (error) throw new Error(`Não foi possível ler o perfil: ${error.message}`);
  return data;
}

export const dataProvider = supabaseDataProvider({
  instanceUrl: supabaseUrl,
  apiKey: supabaseChavePublica,
  supabaseClient: supabase,
});

const base = supabaseAuthProvider(supabase, {
  getIdentity: async (usuario) => {
    const perfil = await lerPerfil(usuario.id);
    return { id: usuario.id, fullName: perfil?.nome ?? usuario.email ?? "" };
  },
  getPermissions: async (usuario): Promise<Permissoes | null> => {
    const perfil = await lerPerfil(usuario.id);
    if (!perfil?.ativo || !perfil.papel) return null;
    return { papel: perfil.papel.codigo === "admin" ? "admin" : "usuario" };
  },
});

/**
 * Usuário desativado continua com sessão no Auth, mas o RLS não lhe mostra nada.
 * Aqui a sessão é encerrada com uma mensagem clara, em vez de telas vazias.
 */
export const authProvider: AuthProvider = {
  ...base,
  async checkAuth(params) {
    await base.checkAuth(params);
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    const perfil = await lerPerfil(data.user.id);
    if (!perfil?.ativo) {
      await supabase.auth.signOut();
      throw new Error("Usuário desativado ou sem cadastro. Fale com o administrador.");
    }
  },
};
